import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { ICouponPrintDiscount } from 'modules/shop/types/coupon';
import { IProductItem } from 'modules/shop/types/product';
import { IStaffItem } from 'modules/shop/types/staff';
import { geneAsyncAction } from 'services/actionConfigs';
import { Message } from 'stompjs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { IMultiCardResDataItem } from '../Cashier/payments/MultiCards/MultipleCardPayment/index.interface';
import { ICustomerReceiveConfigs } from '../Cashier/socket.interface';
import { featureName } from './constants';
import { IBillInfoResData } from './types/bill';
import { IBillOnHoldItem } from './types/ticket';
import { ICashDepositItem } from './types/deposit';
const createAsync = geneAsyncAction(featureName);

const async = {
  newTicket: createAsync('newTicket'),
  lstBillOnHold: createAsync('lstBillOnHold'),
  products: createAsync<string>('products'),
  searchProducts: createAsync<string>('searchProducts'),
  getCardsByTransactionId: createAsync<string>('getCardsByTransactionId'),
  getDetailBill: createAsync<string>('getDetailBill'),
  getDetailBillPreview: createAsync<string>('getDetailBillPreview'),
  getStaffWorking: createAsync<undefined, IStaffItem[]>('getStaffWorking'),
  cashDeposit: createAsync<undefined, ICashDepositItem[]>('cashDeposit'),
  promotions: createAsync<undefined, IProductItem[]>('promotions'),
  getCustomerDetail: createAsync<string>('getCustomerDetail'),
};

export type IcashierReducerState = {
  isLoading: boolean;
  products: IProductItem[],
  isProductLoading: boolean;
  activeCateId: string;
  textSearch?: string;
  searchProducts: IProductItem[];
  lstBillOnHold: IBillOnHoldItem[];
  activeStaffCashier: IStaffItem | null;
  customerSide: {
    configs: ICustomerReceiveConfigs | null,
    message: Message | null,
  },
  cashierMsg: Message | null,
  cardsByTransaction: IMultiCardResDataItem[];
  detailBill: IBillInfoResData | null;
  detailBillPreview: IBillInfoResData | null;

  printerInfo: {
    detail: IBillInfoResData | null;
    coupons: ICouponPrintDiscount[];
  },
  activeItemLatestId: string | null;
  staffsWorking: IStaffItem[];
  cashDeposits: ICashDepositItem[];
  promotion: {
    active: boolean;
    data: IProductItem[];
  },
};

type MyState = IcashierReducerState;

const reducers = {
  init: (state: MyState) => {
    state.isLoading = true;
    state.searchProducts = [];
    state.textSearch = '';
  },
  saveStaffCashier: (state: MyState, { payload }: { payload: IStaffItem | null }) => {
    state.activeStaffCashier = payload;
  },
  saveCSMessage: (state: MyState, { payload }: { payload: Message | null }) => {
    state.customerSide.message = payload;
  },
  saveCSConfigs: (state: MyState, { payload }: { payload: ICustomerReceiveConfigs | null }) => {
    state.customerSide.configs = payload;
  },
  saveCashierMessage: (state: MyState, { payload }: { payload: Message | null }) => {
    state.cashierMsg = payload;
  },
  resetCashierMessage: (state: MyState) => {
    state.cashierMsg = null;
  },

  resetPrinter: (state: MyState) => {
    state.printerInfo.detail = null;
    state.printerInfo.coupons = [];
  },
  saveTicketPrinterDetail: (state: MyState, { payload }: { payload: IBillInfoResData | null }) => {
    state.printerInfo.detail = payload;
  },
  saveTicketPrinterCoupons: (state: MyState, { payload }: { payload: ICouponPrintDiscount[] }) => {
    state.printerInfo.coupons = payload;
  },
  setActiveItemLatest: (state: MyState, { payload }: { payload: string }) => {
    state.activeItemLatestId = payload;
  },
  syncDetailData: (_: MyState, { }: { payload: string }) => { },
  setActivePromotion: (state: MyState, { payload }: { payload: boolean }) => {
    state.promotion.active = payload;
  },
};
const getCurrentState = (state: RootState): MyState => state['payment']; // ex: state['booking']

const selector = <T = MyState>(key: keyof T, defaultValue?: any) =>
  useAppSelector(state => get(getCurrentState(state), key, defaultValue));

const selectors = {
  isLoading: () => selector('isLoading') as MyState['isLoading'],
  textSearch: () => selector('textSearch') as MyState['textSearch'],
  isProductLoading: () => selector('isProductLoading') as MyState['isProductLoading'],
  products: () => selector('products') as MyState['products'] || [],
  activeCateId: () => selector('activeCateId') as MyState['activeCateId'],
  searchProducts: () => selector('searchProducts') as MyState['searchProducts'] || [],
  lstBillOnHold: () => selector('lstBillOnHold') as MyState['lstBillOnHold'] || [],
  activeStaffCashier: () => selector('activeStaffCashier') as MyState['activeStaffCashier'],
  cashierMsg: () => selector('cashierMsg') as MyState['cashierMsg'],
  cardsByTransaction: () => selector('cardsByTransaction') as MyState['cardsByTransaction'],
  detailBill: () => selector('detailBill') as MyState['detailBill'],
  customerSide: () => selector('customerSide') as MyState['customerSide'],
  printerInfo: () => selector('printerInfo') as MyState['printerInfo'],
  detailBillPreview: () => selector('detailBillPreview') as MyState['detailBillPreview'],
  activeItemLatestId: () => selector('activeItemLatestId') as MyState['activeItemLatestId'],
  staffsWorking: () => selector('staffsWorking') as MyState['staffsWorking'],
  cashDeposits: () => selector('cashDeposits') as MyState['cashDeposits'],
  promotion: () => selector('promotion') as MyState['promotion'],
};

const Slice = createSlice({
  name: featureName,
  initialState: {
    isLoading: false,
    products: [],
    isProductLoading: false,
    activeCateId: '',
    textSearch: '',
    searchProducts: [],
    lstBillOnHold: [],
    activeStaffCashier: null,
    customerSide: {
      configs: null,
      message: null,
    },
    cashierMsg: null,
    cardsByTransaction: [],
    detailBill: null,
    printerInfo: {
      detail: null,
      coupons: [],
    },
    detailBillPreview: null,
    activeItemLatestId: null,
    staffsWorking: [],
    cashDeposits: [],
    promotion: {
      active: false,
      data: [],
    }
  } as MyState,
  reducers,
  extraReducers: (builder) => {
    builder
      .addCase(async.promotions.fetch, (state) => {
        state.promotion.data = [];
      })
      .addCase(async.promotions.success, (state, { payload }) => {
        state.promotion.data = payload;
      })
      .addCase(async.promotions.fail, (state) => {
        state.promotion.data = [];
      })

      .addCase(async.cashDeposit.fetch, (state) => {
        state.cashDeposits = [];
      })
      .addCase(async.cashDeposit.success, (state, { payload }) => {
        state.cashDeposits = payload;
      })
      .addCase(async.cashDeposit.fail, (state) => {
        state.cashDeposits = [];
      })

      .addCase(async.getStaffWorking.fetch, (state) => {
        state.staffsWorking = [];
      })
      .addCase(async.getStaffWorking.success, (state, { payload }) => {
        state.staffsWorking = payload;
      })
      .addCase(async.getStaffWorking.fail, (state) => {
        state.staffsWorking = [];
      })


      .addCase(async.getDetailBillPreview.fetch, (state) => {
        state.detailBillPreview = null;
      })
      .addCase(async.getDetailBillPreview.success, (state, { payload }) => {
        state.detailBillPreview = payload;
      })
      .addCase(async.getDetailBillPreview.fail, (state) => {
        state.detailBillPreview = null;
      })

      .addCase(async.getDetailBill.fetch, (state) => {
        state.detailBill = null;
      })
      .addCase(async.getDetailBill.success, (state, { payload }) => {
        state.detailBill = payload;
      })
      .addCase(async.getDetailBill.fail, (state) => {
        state.detailBill = null;
      })

      .addCase(async.getCardsByTransactionId.fetch, (state) => {
        state.cardsByTransaction = [];
      })
      .addCase(async.getCardsByTransactionId.success, (state, { payload }) => {
        state.cardsByTransaction = payload;
      })
      .addCase(async.getCardsByTransactionId.fail, (state) => {
        state.cardsByTransaction = [];
      })

      .addCase(async.lstBillOnHold.success, (state, { payload }) => {
        state.lstBillOnHold = payload;
      })
      .addCase(async.lstBillOnHold.fail, (state) => {
        state.lstBillOnHold = [];
      })

      .addCase(async.searchProducts.fetch, (state, { payload }) => {
        state.textSearch = payload;
        state.isProductLoading = true;
        state.searchProducts = [];
      })
      .addCase(async.searchProducts.success, (state, { payload }) => {
        state.searchProducts = payload;
        state.isProductLoading = false;
      })
      .addCase(async.searchProducts.fail, (state) => {
        state.searchProducts = [];
        state.isProductLoading = false;
      })

      .addCase(async.products.fetch, (state, { payload }) => {
        state.products = [];
        state.activeCateId = payload;
        state.isProductLoading = true;
      })
      .addCase(async.products.success, (state, { payload }) => {
        state.products = payload;
        state.isProductLoading = false;
      })
      .addCase(async.products.fail, (state) => {
        state.products = [];
        state.isProductLoading = false;
      });
  },
});

const actions = {
  ...Slice.actions,
  async,
};
const reducer = Slice.reducer;

export { actions, reducer, selectors };

